import React from 'react';
import styled from 'styled-components/native';
import TextGeneric from '../textGeneric';
import {useTheme} from 'styled-components';
import {LatestNewsProps} from '../../interfaces/components/latestNews';
import {Theme} from '../../theme/lightTheme';
import {hdp} from '../../utils/responsive';
import {removeHtmlTags} from '../../utils/helpers';
import Icon from '../icon';

const Container = styled.TouchableOpacity<{isSmall?: boolean}>`
  width: 100%;
  min-height: ${({isSmall}) => hdp(isSmall ? 5 : 10)}px;
  border-radius: ${({isSmall, theme}) =>
    isSmall ? theme.text.s55 / 2 : theme.text.s55}px;
  overflow: hidden;
  background-color: ${({theme}) => theme.quickAccess.background};
`;
const ContentContainer = styled.View`
  flex: 1;
  flex-direction: row;
  padding-vertical: ${({theme}) => theme.text.s12}px;
  padding-left: ${({theme}) => theme.text.s12}px;
`;
const TitleContainer = styled.View<{isSmall?: boolean}>`
  margin-right: 5%;
  ${({isSmall}) =>
    isSmall &&
    `
     flex: 1;
  align-items: center;
  justify-content: center;
    `};
`;
const Title = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s6}px;
  color: ${({theme}) => theme.text.dark};
  width: 95%;
`;
const ImageContainer = styled.View<{selectedColor: string}>`
  height: 100%;
  aspect-ratio: 1;
  border-radius: ${({theme}) => theme.text.s55}px;
  overflow: hidden;
  margin-right: ${({theme}) => theme.text.s7}px;
  align-items: center;
  justify-content: center;
  background-color: ${({selectedColor}) =>
    selectedColor ? selectedColor : 'transparent'};
`;
const ImageContent = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s9}px;
  color: ${({theme}) => theme.text.light};
  text-align: center;
  width: 80%;
  align-self: center;
`;
const DetailsContainer = styled.View<any>`
  flex: 1;
  justify-content: center;
  ${({isExtraPadding}) =>
    isExtraPadding &&
    `
    padding-horizontal:3%;
    `}
`;
const IconContainer = styled.View`
  padding-horizontal: ${({theme}) => theme.text.s6}px;
  height: 100%;
  align-items: center;
  justify-content: center;
`;
const colors = [
  '#203476',
  '#1A1AF5',
  '#5151F7',
  '#6C00AA',
  '#8C3ABB',
  '#8080F9',
  '#71B8CF',
  '#3A9EBD',
  '#0084AD',
  '#59C7E6',
  '#7FDBC1',
  '#4ECFAC',
  '#27B9E0',
  '#18C296',
];

/**
 * GenioLatestNews Component
 *
 * The `GenioLatestNews` component represents a card-style view for displaying the latest news.
 * It includes an image, a title, and a description. The component is designed to be touchable
 * and supports an onPress callback for interaction.
 *
 * @component
 *
 * @param {ImageSourcePropType} image - The image associated with the news item.
 * @param {string} title - The title of the latest news item.
 * @param {TextStyle} [titleStyle] - Additional styles to be applied to the title text.
 * @param {string} description - The description or summary of the latest news item.
 * @param {TextStyle} [descriptionStyle] - Additional styles to be applied to the description text.
 * @param {ViewStyle} style - Additional styles to be applied to the overall component container.
 * @param {() => void} [onPress] - Callback function to be executed when the news item is pressed.
 *
 * @param isSmall
 * @returns {React.ReactElement} LatestNews component.
 */
const GenioLatestNews = ({
  image,
  title,
  specialty,
  index = 0,
  titleStyle,
  style,
  onPress,
  isSmall,
  customLeftComponent,
}: LatestNewsProps): React.ReactElement => {
  const theme = useTheme() as Theme;
  return (
    <Container
      isSmall={isSmall}
      activeOpacity={0.8}
      disabled={!onPress}
      onPress={onPress}
      style={style}>
      <ContentContainer>
        {customLeftComponent ? (
          customLeftComponent()
        ) : specialty ? (
          <ImageContainer selectedColor={colors[index % (colors.length - 1)]}>
            <ImageContent source={image} numberOfLines={2}>
              {specialty || ''}
            </ImageContent>
          </ImageContainer>
        ) : null}
        <DetailsContainer isExtraPadding={!image}>
          {!!title && (
            <TitleContainer isSmall={isSmall}>
              <Title semiBold style={titleStyle} numberOfLines={2}>
                {removeHtmlTags(title)}
              </Title>
            </TitleContainer>
          )}
        </DetailsContainer>
        <IconContainer>
          <Icon
            name={'navArrow'}
            size={isSmall ? theme.text.s3 : theme.text.s1}
            color={theme.home.cardBackground + '77'}
            stroke={theme.text.dark}
          />
        </IconContainer>
      </ContentContainer>
    </Container>
  );
};

export default GenioLatestNews;
